/**
 * Checkout — one purchase attempt, one document, forever. A doc is created
 * the instant the event page lands (buyer branch) or a member issues guest
 * tickets (guest branch), marches forward through `status`, and stops: a
 * terminal doc is never written again by anyone. Buy-again and retry are
 * NEW documents, never transitions — the words "restart" and "cycle" are
 * not part of this domain. `guest` is the member-issued system kind —
 * skipped by preparePayment (no PSP leg); settleOrder skips its capture
 * call. Absorbs the webhook dedup ledger (`processedEvents`) — no order
 * exists until settle, so this is where it lives.
 *
 * Path: `workspaces/{orgId}/events/{eventId}/checkouts/{checkoutId}` — doc
 * id is client-minted random on the buyer branch (rules pin `buyerUid ==
 * auth.uid` instead of the doc id), or the deterministic system id
 * (`guest__{id}`) on the guest branch, own-doc read there pinned to `issuedBy
 * == auth.uid` instead — the issuing member's own outcome watch, since
 * refusal is a Checkout-only terminal state no Order is ever minted for.
 * Settle mints the order AT THIS SAME ID (`orders/{checkoutId}`) — the
 * checkout↔order join is the identity itself; no orderId field exists.
 *
 * `status` is the ONE discriminant every reader branches on. The
 * timestamps beside it are an audit trail written in the same transaction
 * as their transition, and nobody's control flow reads them — `expiresAt`
 * alone is not audit: it is the retention deadline, written beside
 * `closedAt` and read by the collection-group TTL. Sole writer
 * per transition:
 *
 *   pricing → payable       preparePayment (mints the PSP session)
 *   payable → authorising   the payment-event core (webhook CAS; paymentPoll re-drives it)
 *   authorising → paid      settleOrder (captures; mints orders/{checkoutId})
 *   authorising → refused   settleOrder (refuses; voids the hold)
 *   pricing|payable → dead  the buyer's own CAS, retireSessions, or paymentPoll
 *
 * Guest docs are born `authorising` (authorised by fiat at create — no
 * PSP leg) and settle from there exactly as a buyer doc does; so is the
 * fresh doc a buyer's free confirm creates, the same shape minus issuedBy.
 *
 * A €0 basket gets no PSP session, so its `pricing` doc is the one state
 * nothing else advances — paymentPoll retires it past the engine's session
 * budget, since a doc that never closes is never stamped `expiresAt`, and one
 * without `expiresAt` is one the TTL never collects, and it carries
 * `payload.buyer`.
 *
 * The write rules — create (both branches), the items/contact edit, and the
 * retire CAS — live in a hand fragment (`ican.extraMatches`): the
 * `payload` field is a nested map, and `.writes(...)`'s auto-derived field
 * mask only ever covers TOP-LEVEL keys, so the "payload's own sub-keys are
 * closed to {items,buyer,channel,voucher}" constraint is not expressible
 * through the kernel write-pattern DSL. `Checkout`'s own `entity()`
 * therefore declares only `read` — the own-doc listener that is checkout's
 * entire outcome mechanism — so nothing generated competes with the
 * fragment (the same pattern `WorkspaceSettings` uses for
 * `invoiceCounter`).
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import {
	ChannelSchema,
	LocaleSchema,
	OrderLineSchema,
	PaymentModeSchema,
	PspProviderSchema,
} from "./order.entity.ts";
import { Cents, Qty } from "./ticket-type.entity.ts";
import type { Actor } from "./workspace.entity.ts";

export const BuyerContactSchema = z
	.object({
		email: z.string().email(),
		name: z.string().min(1),
		address: z
			.string()
			.min(1)
			.nullish()
			.describe(
				"the recipient's postal address, one free-text block — the storefront asks for it only once the basket crosses `needsRecipientAddress` (order.entity.ts), and settleOrder refuses an above-ceiling checkout that reaches it without one",
			),
		locale: LocaleSchema,
	})
	.describe(
		"the buyer identity carried on the checkout’s payload — settleOrder reads it at settle and stamps its fields onto the order flat, as buyerEmail/buyerName/buyerAddress/buyerLocale, never as a nested object",
	);

export const RefusalReasonSchema = z
	.enum(["soldOut", "notEligible", "internal", "licenceReadOnly"])
	.describe(
		'why a checkout was refused, opaque by design — window, cap, voucher, absent contact, and a stale authorised amount (an items edit that outraced the buyer’s own payment confirm) all collapse into notEligible so the outcome never becomes a validity oracle; a quantity-only soldOut against a type whose public bucket already read low renders as "that quantity is no longer available" in copy, never "sold out", since the storefront already showed low before the buyer committed; the stale-amount case renders as "your basket changed before payment went through — nothing was charged, try again", never a technical mismatch message; licenceReadOnly is the deployment’s licence having lapsed into read-only — a fact about the whole shop rather than this basket, so naming it reveals nothing about any code or type, and it renders as sales being paused',
	);
export type RefusalReason = z.infer<typeof RefusalReasonSchema>;

/**
 * The one discriminant. Forward-only — no state has an edge back:
 * `paid`, `refused`, and `dead` are terminal, and a new purchase is a new
 * document. A pre-authorisation card decline is deliberately NOT a state
 * (`declinedAt` is audit-only; the doc stays `payable`): the PSP order is
 * live and the mounted widget retries inside it.
 */
export const CheckoutStatusSchema = z.enum([
	"pricing", // doc exists; preparePayment has not answered a PSP session yet
	"payable", // a live PSP session — the ONLY state the storefront mounts a widget from
	"authorising", // the PSP confirmed an authorisation; settle owns the doc from here
	"paid", // terminal — captured; the order lives at orders/{checkoutId}
	"refused", // terminal — settle refused and voided the hold, or preparePayment refused a pricing doc under a read-only licence
	"dead", // terminal — superseded at land, buyer left, or the sweep retired it
]);
export type CheckoutStatus = z.infer<typeof CheckoutStatusSchema>;

/** The states a buyer's client may still write (edit payload, retire). */
export const OPEN_STATUSES = ["pricing", "payable"] as const;

/** The states with no outgoing edges — never written again, by anyone. */
export const TERMINAL_STATUSES = ["paid", "refused", "dead"] as const;

/**
 * How long a closed checkout stays readable — the window both published
 * privacy notices disclose, and the offset `expiresAt` is stamped off
 * `closedAt`. Seconds rather than millis because every writer holds a
 * `Timestamp` and adds whole seconds to its `seconds` field, carrying its
 * `nanoseconds` through untouched: `expiresAt − closedAt` is then exactly
 * this window, not this window less whatever a millisecond round trip
 * truncated.
 */
export const RETENTION_SECONDS = 30 * 24 * 60 * 60;

/**
 * What the server resolved `payload.voucher` to — the whole of what a browser
 * is ever allowed to know about a code, since no client may read a voucher
 * document at any tier.
 *
 * A union on `status` rather than one object with a tag: a refusal has no
 * discount and no revealed types to carry, and a flat shape would leave it
 * holding two fields that mean nothing and are free to say something. The
 * refused branch carries no cause either — naming one would turn the code
 * field into an oracle over which codes exist, when they expire and how many
 * redemptions each has left (voucher.entity.ts's resolveVoucher makes that
 * decision, and makes it as one indistinguishable answer).
 *
 * `code` rides both branches because a result is read against the code the
 * buyer currently has typed: one naming a different code is the previous
 * answer, which is what a surface reads as still-resolving rather than as this
 * code's verdict.
 *
 * `discountCents` is carried here rather than summed from `pricedLines`, where
 * an order sums its own lines instead: `pricedLines` is client-writable — the
 * buyer's device re-prices the basket on every edit — so a discount line can
 * be gone from it while this server-written answer still stands, which is the
 * applied-and-re-resolving face. An order's lines are written once at settle
 * and never again, so there the sum IS the figure and no copy is kept beside
 * them.
 */
export const VoucherResultSchema = z.discriminatedUnion("status", [
	z.object({
		status: z.literal("applied"),
		code: z.string(),
		discountCents: Cents.describe(
			"what the code takes off this checkout's eligible lines, in cents — the server's per-VAT-rate discount lines summed, so a buyer reading a basket is shown the amount rather than the apportionment. Zero is a real applied answer and a common one: a code that only reveals hidden ticket types, and one whose discount has nothing eligible in the basket yet, both apply at zero — applied means the code was accepted, never that money came off",
		),
		unlockedTypeIds: z
			.array(z.string())
			.describe(
				"the ticket types this code reveals to this checkout — a type whose claimableBy resolves 'code' is hidden from the public shop and offered only where this array names it. Empty on a code that only takes money off",
			),
	}),
	z.object({
		status: z.literal("refused"),
		code: z.string(),
	}),
]);
export type VoucherResult = z.infer<typeof VoucherResultSchema>;

// events/{eventId}/checkouts/{checkoutId} — one purchase attempt. Takes
// zero capacity at start — capacity is touched only inside settleOrder.
export const CheckoutSchema = z.object({
	status: CheckoutStatusSchema,
	buyerUid: z
		.string()
		.nullable()
		.describe(
			"rules-pinned to auth.uid at create on the buyer arm — the read/edit/retire door, since the doc id is a client-minted random id, not the uid; null on the guest arm (issuedBy names the actor there)",
		),
	payload: z.object({
		eventId: z.string(),
		items: z
			.array(z.object({ typeId: z.string(), qty: Qty }))
			.min(1)
			.describe(
				"auto-selected at land — the first currently-purchasable type, qty 1, never empty — the buyer adjusts freely from there, single- and multi-type events alike",
			),
		buyer: BuyerContactSchema.nullish().describe(
			"absent at create — nothing is known about the buyer yet. The FE writes the complete object back once all three fields validate (debounced ~300–500ms), and again on every later correction — never a partial write, so a session either recovers a buyer’s contact whole, or not at all. settleOrder requires this field present and refuses notEligible if it is absent",
		),
		channel: ChannelSchema.nullish(),
		voucher: z
			.string()
			.nullish()
			.describe("a voucher code to consume inside the settle transaction"),
	}),
	startedAt: z
		.custom<Timestamp>()
		.describe(
			"rules-pinned == request.time at create — the staleness bound paymentPoll and retireSessions range on, beside the status equality",
		),
	pricedLines: z
		.array(OrderLineSchema)
		.nullish()
		.describe(
			"priced from the type’s live price at create and re-priced on every later items edit alike (guest arm: unitPrice 0, fixed at create, never re-priced); settle re-validates each line’s capacity fit independently and requires the authorised payment amount to still match this array’s CURRENT total, refusing a mismatch as notEligible",
		),
	voucherResult: VoucherResultSchema.nullish().describe(
		"the server's own answer about payload.voucher, written by preparePayment in the same transaction as the pricedLines it re-priced — the storefront's only source for the code field's applied and refused faces, and for which hidden ticket types this checkout may buy. Absent until a code has been resolved on this checkout",
	),
	// The floor is the whole enforcement. Every client UPDATE is masked to
	// payload/pricedLines by the hand-written rule fragment, so no client
	// writes this field on an existing document; a create carrying a negative
	// value fails this parse on the server's own read, which is what keeps the
	// cap from being bought out at birth by a client rules cannot check values
	// on.
	voucherAttempts: z
		.number()
		.int()
		.min(0)
		.default(0)
		.describe(
			"how many code submissions this checkout has had refused — what VOUCHER_ATTEMPT_CAP (voucher.entity.ts) bounds, so walking the obvious guesses costs a fresh checkout every few tries. It counts submissions, never resolutions: a code that already stands is re-resolved on every payload change while the buyer edits the basket under it, and none of those re-answers is an attempt",
		),
	payment: z
		.object({
			provider: PspProviderSchema,
			id: z.string(),
			clientToken: z.string(),
			env: PaymentModeSchema.nullish().describe(
				"the Revolut environment this checkout's payment session was created under, stamped once by preparePayment at session-create and never re-derived — settle carries this value onto the order's own payment.env instead of re-resolving the runtime active mode, so an operator's mid-checkout mode flip can never redirect an already-authorised session's capture/void calls to the wrong host",
			),
			publicKey: z
				.string()
				.min(1)
				.nullish()
				.describe(
					"the publishable key of the Revolut account this session was created on, stamped by preparePayment beside env — the organization's own key for that environment, else the deployment's sandbox key when the session runs on it. The storefront's wallet row mounts against it, ahead of the card's payment.publicKey, so an organization checking out on the deployment's sandbox offers wallets without a key stamped on its card",
				),
			syncedAmount: Cents.nullish().describe(
				"the total the PSP order is CURRENTLY priced at, minor units — stamped by preparePayment at session-create and again after every successful re-price PATCH, so it trails pricedLines' own total by the round-trip to Revolut. This is the storefront's only signal that the PSP side has caught up with an items edit: the mounted widgets cache the order's amount at construction (the express instances in their paymentRequest call, the card field in the order the SDK reads at RevolutCheckout(token)), and Revolut answers a payment submitted at a stale amount with a 400, so the storefront gates the pay control on THIS matching the basket and rebuilds the widgets when it moves — never on pricedLines' total, which is already the new value while Revolut still holds the old",
			),
			authorisedAmount: Cents.nullish().describe(
				"the PSP-confirmed authorised total, minor units — read via order retrieval (GET /api/orders/{id}’s payments[].authorised_amount, summed across every attempt Revolut marked authorised; not the order’s own top-level amount, which merely echoes the order’s configured price rather than what was PSP-confirmed) and stamped by the payment-event core in the same transaction as the payable → authorising transition; null until authorised. Settle compares this against pricedLines’ CURRENT total, refusing a mismatch notEligible — the amount race (an items edit landing after the buyer’s own payment confirm)",
			),
			processingSince: z
				.custom<Timestamp>()
				.nullish()
				.describe(
					"when a reconciler FIRST observed the PSP order sitting in processing, cleared again by the first observation of any other state — the abandonment clock paymentPoll times an orphaned attempt from, since startedAt dates the buyer opening the shop rather than the attempt: a buyer who browsed twenty minutes and then entered a 3DS challenge is one minute into paying, not twenty. Also the guard value the stamp-dead CAS re-reads and compares before it retires the doc — processing is not monotonic (a concluded or abandoned attempt reverts the order to pending), so a clear or a re-stamp landing between the poll and the commit aborts the kill",
				),
		})
		.nullish()
		.describe(
			"absent on the guest arm; payment.id is the identity guard — a webhook whose order id doesn’t match the doc's payment.id is foreign to this document and is voided, never silently no-opped",
		),
	processedEvents: z
		.record(z.string(), z.object({ receivedAt: z.custom<Timestamp>() }))
		.default({})
		.describe(
			"the webhook dedup ledger, keyed revolut__{event}__{paymentId} — no order exists until settle, so this is where the ledger lives; the webhook’s transaction creates the entry (abort if the key exists) in the same transaction as the payable → authorising CAS, making a redelivered webhook a no-op; per document, so no cross-cycle freeze semantics exist",
		),
	issuedBy: z
		.string()
		.nullish()
		.describe(
			"the member uid on the guest arm; absent on the buyer arm — rules pin it to auth.uid at create",
		),
	authorisedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"audit — written in the same transaction as payable → authorising. Guest arm: stamped by rules at create (== request.time), the doc being born authorising",
		),
	settledAt: z
		.custom<Timestamp>()
		.nullish()
		.describe("audit — written in the same transaction as authorising → paid"),
	refusedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"audit — written in the same transaction as authorising → refused, or as pricing → refused when a read-only licence refuses a basket before any session exists",
		),
	refusalReason: RefusalReasonSchema.nullish().describe(
		"carries notEligible (window/cap/voucher/absent-contact/stale-amount), soldOut (capacity) and licenceReadOnly (the deployment's licence lapsed — sales paused) alike — real payload the buyer’s copy reads, not a bare sentinel",
	),
	declinedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"audit ONLY, never a state — stamped by the payment-event core on ORDER_PAYMENT_DECLINED/FAILED while the doc stays payable; the widget retries within the SAME Revolut order",
		),
	expiredAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"audit — written when paymentPoll or retireSessions moved a payable doc to dead because its PSP order was gone, expired, or its environment was retired",
		),
	abandonedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"audit — written by the buyer's own retire CAS (supersede-at-land, or the shop page's navigate-away lifecycle) in the same write as pricing|payable → dead",
		),
	closedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"audit — the instant this checkout closed, stamped at every terminal transition alike; the per-transition stamp beside it (settledAt, refusedAt, expiredAt, abandonedAt) names which one closed it",
		),
	expiresAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"when this checkout becomes collectable — closedAt + 30 days, written in the same update that stamps closedAt, at every terminal transition alike. The field the checkouts collection-group TTL keys on, and the thirty days sit in the document rather than on the policy: Firestore collects once the keyed timestamp is past, so a policy keyed on closedAt — already past at write — collects within a day against the thirty-day window both privacy notices disclose. The platform does carry an expiration offset on the policy, but firebase-tools cannot declare one (a field override's `ttl` is a boolean) and clears a hand-set one on the next indexes deploy, whose drift check compares presence alone. Storing the offset is what makes the window survive a routine deploy",
		),
});
export type Checkout = z.infer<typeof CheckoutSchema>;
// A writer that omits a .default()-bearing field (processedEvents,
// voucherAttempts) and relies on CheckoutSchema.parse to fill it types its own
// literal against this z.input, not z.infer's output — the same literal typed
// against Checkout would demand fields the writer never sets.
export type CheckoutInput = z.input<typeof CheckoutSchema>;

// Own-doc read/listen — the entire outcome mechanism of checkout. The doc
// id is a client-minted random id, so ownership is the buyerUid field, not
// the path: data.buyerUid == me.uid. Touches resource.data, so the
// transpiler emits `allow get:` only — sufficient, the buyer listens by an
// id they hold (the device pointer), never by query. Guest docs
// (buyerUid == null) mirror the same shape on issuedBy: the issuing member
// reads their own submission's outcome by data.issuedBy == me.uid, equally
// get-only — nobody else reads a guest checkout by query.
export const Checkout = entity<Actor>()({
	schema: CheckoutSchema,
	actions: ["read"] as const,
})
	.pipe(({ can }) => [
		can("read").when((data, me) => data.buyerUid.eq(me.uid)),
		can("read").when((data, me) => data.issuedBy.eq(me.uid)),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/checkouts/{checkoutId}",
		}),
	);
